import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type ApiError, setAuthToken } from '../lib/api';

interface RedeemResp {
  candidate: { id: string; name: string; email: string };
  phase: 'benchmark' | 'dojo';
  expiresAt: string;
  token: string;
}

interface StartResp {
  attemptId: string;
}

export function RedeemCode() {
  const params = useParams<{ code?: string }>();
  const [code, setCode] = useState(params.code ?? '');
  const [info, setInfo] = useState<RedeemResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const redeem = async (raw: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<RedeemResp>('/api/take/redeem', { code: raw });
      setAuthToken(r.token);
      setInfo(r);
    } catch (e) {
      const err = e as ApiError;
      const body = err.body as { error?: string } | undefined;
      if (body?.error === 'code-expired') {
        setError('This access code has expired. Please request a new code.');
      } else if (body?.error === 'code-already-used') {
        setError('This access code is already used for a completed attempt. Ask admin for a new code.');
      } else if (body?.error === 'code-not-found') {
        setError('Access code not found. Please verify and try again.');
      } else {
        setError('Invalid or expired access code.');
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (params.code) redeem(params.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.code]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    redeem(code.trim());
  };

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<StartResp>('/api/take/start');
      navigate(`/attempt/${r.attemptId}`);
    } catch (e) {
      const body = (e as { body?: { error?: string; message?: string } }).body;
      setError(body?.message ?? body?.error ?? 'Failed to start the test. Please try again or contact the administrator.');
    } finally {
      setBusy(false);
    }
  };

  if (info) {
    return (
      <div className="page-narrow">
        <h1>Ready, {info.candidate.name}?</h1>
        <p>
          You're about to start the{' '}
          <strong>{info.phase === 'benchmark' ? 'Benchmark' : 'Dojo'}</strong>. Once you
          start, the timer cannot be paused.
        </p>
        <button className="btn btn-primary" disabled={busy} onClick={start}>
          Start the test
        </button>
        {error && <p className="error" style={{ marginTop: 16 }}>{error}</p>}
      </div>
    );
  }

  return (
    <div className="page-narrow">
      <h1>Enter your access code</h1>
      <p>You should have received an email with a code like <code>ABCD-1234</code>.</p>
      <form onSubmit={submit} className="redeem-form">
        <input
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="ABCD-1234"
          aria-label="Access code"
        />
        <button className="btn btn-primary" type="submit" disabled={busy || code.length < 4}>
          Continue
        </button>
      </form>
      {error && <p className="error">{error}</p>}
    </div>
  );
}
